<template>
  <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4">
    <div class="max-w-md w-full space-y-6 p-8 bg-white rounded-xl shadow-2xl">
      <!-- Header -->
      <div class="text-center">
        <div class="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
          <ShieldCheckIcon class="h-10 w-10 text-red-600" />
        </div>
        <h2 class="mt-6 text-3xl font-extrabold text-gray-900">
          Site Owner Login
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          Platform Administrative Access
        </p>
      </div>

      <!-- Security Warning Banner -->
      <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <ExclamationTriangleIcon class="h-5 w-5 text-yellow-400" />
          </div>
          <div class="ml-3">
            <p class="text-sm text-yellow-700">
              <strong>Authorized Access Only</strong><br>
              All login attempts are logged and monitored. Unauthorized access attempts will be reported.
            </p>
          </div>
        </div>
      </div>

      <!-- Login Form -->
      <form @submit.prevent="submit" class="space-y-5">
        <div class="space-y-3">
          <!-- Email Field -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              autocomplete="email"
              autofocus
              class="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
              :class="{ 'border-red-500': form.errors.email }"
            />
            <p v-if="form.errors.email" class="mt-1 text-sm text-red-600">
              {{ form.errors.email }}
            </p>
          </div>

          <!-- Password Field -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              v-model="form.password"
              type="password"
              required
              autocomplete="current-password"
              class="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
              :class="{ 'border-red-500': form.errors.password }"
            />
            <p v-if="form.errors.password" class="mt-1 text-sm text-red-600">
              {{ form.errors.password }}
            </p>
          </div>
        </div>

        <!-- Remember Me Checkbox -->
        <div class="flex items-center">
          <input
            id="remember"
            v-model="form.remember"
            type="checkbox"
            class="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
          />
          <label for="remember" class="ml-2 block text-sm text-gray-900">
            Remember me for 30 days
          </label>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="form.processing"
          class="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <span v-if="!form.processing">Sign In</span>
          <span v-else class="flex items-center">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Signing in...
          </span>
        </button>
      </form>

      <!-- Footer Information -->
      <div class="text-center text-xs text-gray-500 space-y-1 pt-3">
        <p>Contact system administrator for access issues</p>
        <p class="font-mono bg-gray-100 px-2 py-1 rounded">
          IP: {{ userIp }} | Session: {{ sessionId }}
        </p>
      </div>

      <!-- Back to Customer Login Link -->
      <div class="text-center pt-3 border-t border-gray-200">
        <a
          href="/login"
          class="text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center transition-colors"
        >
          <ArrowLeftIcon class="h-4 w-4 mr-1" />
          Go to Customer Login
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3';
import { ShieldCheckIcon, ExclamationTriangleIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  userIp: {
    type: String,
    required: true,
  },
});

const form = useForm({
  email: '',
  password: '',
  remember: false,
});

const sessionId = Math.random().toString(36).substring(7).toUpperCase();

const submit = () => {
  form.post('/site-owner-administrator/login', {
    onFinish: () => {
      form.reset('password');
    },
  });
};
</script>
